// @flow
import type { Media } from 'types/media';
import type { StoreState } from 'reducers';
import type { RequestStates } from 'types/redux';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import i18n from 'i18n-js';
import _ from 'lodash';

import { Message, Header, Grid, Button, Popup } from 'semantic-grab-ui/src';

import { uploadMedium, UPLOAD_MEDIUM, replaceMedium, REPLACE_MEDIUM } from 'actions/media';
import { resetRequestState } from 'actions/helpers';
import ExampleModal from 'components/modals/exampleModal';
import placeholder from 'img/placeholder.png';
import './ImageUpload.scss';


const backgroundSizeRatio = 0.6;

type Props = {
  medium: Media,
  newlyCreatedObject: Media,
  uploadMedium: (_: any, _: string) => void,
  uploadMediumRequest: RequestStates,
  replaceMedium: (_: string, _: any, _: string) => void,
  replaceMediumRequest: RequestStates,
  resetRequestState: (_: string | Array<string>) => void,
  onImageChange: (_: ?Media) => void,
  maxFileSize: number, // in kb
  maxImageSize: Array<number>, // in order width x height (px)
  supportedFileExtentions: Array<string>,
  title: string,
  description?: string,
  required?: boolean,
  showAllError: boolean,
  replaceMedia?: boolean,
  formName: string,
  requestSource: string,
  examplePopup?: any, // shows as a popup for example how it would look like in real life
  exampleModal?: any, // shows as a modal for example how it would look like in real life
  viewOnly?: boolean,
  imagePath?: string,
};

export class ImageUpload extends Component {
  props: Props;

  static defaultProps = {
    required: false,
    replaceMedia: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = { errorMessage: '' };
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.showAllError && this.props.required) {
      this.setState({ errorMessage: _.get(nextProps, 'medium.mediumID') ? '' : i18n.t('errors.image_required') });
    }
    const uploadSuccess = nextProps.uploadMediumRequest.isSuccessful;
    const isTheSameSource = this.isOriginResource(nextProps.formName, nextProps.requestSource);

    if (uploadSuccess && isTheSameSource) {
      this.setState({ errorMessage: '' });
      this.props.resetRequestState(UPLOAD_MEDIUM);
      this.props.onImageChange(nextProps.newlyCreatedObject);
    }

    const replaceSuccess = nextProps.replaceMediumRequest.isSuccessful;
    if (replaceSuccess && isTheSameSource) {
      this.setState({ errorMessage: '' });
      this.props.resetRequestState(REPLACE_MEDIUM);
      this.props.onImageChange(nextProps.newlyCreatedObject);
    }
  }

  isValidDimension(image: Image) {
    const { width, height } = image;
    const { maxWidth, maxHeight } = this.imageSizeConstraint();

    return width <= maxWidth && height <= maxHeight;
  }

  isValidSize(file: File) {
    const { size } = file;
    const { maxFileSize } = this.props;
    return size / 1000 <= maxFileSize;
  }

  // Use to distinguish request betwen multiple image upload form
  isOriginResource(formName: string, requestSource: string): boolean {
    return formName === requestSource;
  }

  handleImageChange = (event: any) => {
    const reader = new FileReader();
    const file = event.target.files[0];

    // In case users cancel/close the file selector.
    if (!file) {
      return;
    }

    if (!this.isValidSize(file)) {
      const errorMessage = i18n.t('errors.file_size');
      this.setState({ errorMessage });
      return;
    }

    // We use callback `onload` here to get the image's size
    reader.onload = (event: any) => {
      const image = new Image();
      image.src = event.target.result;

      image.onload = () => {
        if (!this.isValidDimension(image)) {
          this.setState({ errorMessage: i18n.t('errors.image_size') });
          return;
        }

        this.uploadMedium(file);
      };

      this.resetFileValue();
    };

    reader.readAsDataURL(file);
  };

  resetFileValue(): void {
    // In case user re-upload the same file (upload => delete => upload);
    this.file.value = '';
  }

  uploadMedium(file: Object): void {
    const form = new FormData();
    form.append('file', file);

    if (this.props.replaceMedia && _.get(this.props.medium, 'path', false)) {
      const oldFileName = _.split(_.last(_.split(this.props.medium.path, '/')), '.')[0];
      this.props.replaceMedium(oldFileName, form, this.props.formName);
    } else {
      this.props.uploadMedium(form, this.props.formName);
    }
  }

  handleUploadForm = () => {
    if (!this.props.viewOnly) {
      this.file.click();
    }
  };

  handleDeleteImage = () => {
    this.setState({ errorMessage: this.props.required ? i18n.t('errors.image_required') : '' });
    this.props.onImageChange(null);
  };

  isImageExists(): boolean {
    return _.get(this.props.medium, 'mediumID') > 0 || (this.props.imagePath !== undefined && this.props.imagePath !== '');
  }

  getImagePath(): any {
    return _.get(this.props.medium, 'path') || this.props.imagePath || placeholder;
  }

  hasErrorMessage(): boolean {
    return this.state.errorMessage !== '';
  }

  mainContent() {
    const { required, replaceMedia, formName, viewOnly } = this.props;
    const { maxWidth, maxHeight } = this.imageSizeConstraint();
    const requiredClassName = required ? 'required' : '';
    const imageSrc = this.getImagePath();
    const resizedWidth = formName === 'SubscriptionHero' ? 600 * backgroundSizeRatio : maxWidth * backgroundSizeRatio;
    const resizedHeight = formName === 'SubscriptionHero' ? 336 * backgroundSizeRatio : maxHeight * backgroundSizeRatio;
    const imgStyle = {
      maxWidth: resizedWidth,
      maxHeight: resizedHeight,
      width: '100%',
    };
    const uploadBtnStyle = {
      backgroundImage: `url('${this.getImagePath()}')`,
      maxWidth: resizedWidth,
      maxHeight: resizedHeight,
      width: '100%',
    };

    if (this.isImageExists()) {
      return (
        <div>
          <a className={requiredClassName} onClick={this.handleUploadForm}>
            <img src={imageSrc} alt="cover" style={imgStyle} />
          </a>
          {!replaceMedia && !viewOnly &&
            <a className="link delete-image" onClick={this.handleDeleteImage}>
              {i18n.t('entities.delete')}
            </a>
          }
        </div>
      );
    }

    return (
      <Button
        className={`upload-btn ${requiredClassName}`}
        onClick={this.handleUploadForm}
        content={i18n.t('entities.upload')}
        style={uploadBtnStyle}
        disabled={viewOnly}
      />
    );
  }

  // Just a way to unified maxWidth, maxHeight access
  // It should have been separated props
  imageSizeConstraint() {
    const { maxImageSize } = this.props;
    const [maxWidth, maxHeight] = maxImageSize;

    return { maxWidth, maxHeight };
  }

  renderExamplePopup() {
    if (this.props.examplePopup) {
      return (
        <Popup
          position="right center"
          trigger={<Button basic>{i18n.t('rewards_page.form.example')}</Button>}
          content={this.props.examplePopup}
          on="click"
          hideOnScroll
        />
      );
    }

    return null;
  }

  render() {
    const { supportedFileExtentions, maxFileSize, title, description, viewOnly } = this.props;
    const { maxWidth, maxHeight } = this.imageSizeConstraint();
    const localedImageMaxSize = `${maxWidth} x ${maxHeight} (pixel)`;

    const localedSupportedFileExtentions = i18n.t('brands_page.form.image.file_extension', {
      extensions: supportedFileExtentions.join('/'),
    });

    const acceptedFileExtensions = _.map(supportedFileExtentions, extension => `.${extension}`).join(',');

    return (
      <Grid className="image-upload">
        <Grid.Row>
          <Grid.Column>
            <Message negative hidden={!this.hasErrorMessage()}>
              <p>{this.state.errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={4}>
            <Header className="inline" as="h3">
              {title}
              {description && <Header.Subheader>{description}</Header.Subheader>}
            </Header>
            <Header as="h5">
              {i18n.t('brands_page.form.image.dimension')}
              <Header.Subheader>
                {localedImageMaxSize}
              </Header.Subheader>
            </Header>

            <Header as="h5">
              {localedSupportedFileExtentions}
              <Header.Subheader>
                {`${maxFileSize}KB`}
              </Header.Subheader>
            </Header>
            {this.renderExamplePopup()}
            <ExampleModal content={this.props.exampleModal} />
          </Grid.Column>

          <Grid.Column width={12}>
            <input
              type="file"
              name="image"
              onChange={this.handleImageChange}
              accept={acceptedFileExtensions}
              ref={file => (this.file = file)}
              disabled={viewOnly}
            />

            {this.mainContent()}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export function mapStateToProps(state: StoreState) {
  return {
    newlyCreatedObject: state.media.newlyCreatedObject,
    uploadMediumRequest: state.requests.uploadMediumRequest || {},
    replaceMediumRequest: state.requests.replaceMediumRequest || {},
    requestSource: state.media.requestSource,
  };
}

export default connect(mapStateToProps, {
  uploadMedium,
  replaceMedium,
  resetRequestState,
})(ImageUpload);
